<template>
  <div class="rpt-page">
    <div class="rpt-head">
      <div>
        <div class="rpt-crumb">Reportes</div>
        <h2 class="rpt-title">Volumen y valor por cliente</h2>
        <p class="rpt-subtitle">
          Facturas, monto CIF y desempeño por laboratorio y por distribuidora en
          el periodo. El ciclo se mide de recepción a salida de Farmacia y
          Drogas, que es el tramo con histórico completo.
        </p>
      </div>
      <div class="rpt-actions">
        <v-btn
          icon
          to="/reportes/guia"
          title="Cómo leer este reporte"
          class="rpt-help"
        >
          <v-icon>help_outline</v-icon>
        </v-btn>
        <v-btn
          class="rpt-btn--ghost"
          depressed
          :disabled="!laboratorios.length"
          @click="exportar"
        >
          <v-icon left small>file_download</v-icon>Exportar
        </v-btn>
        <v-btn class="rpt-btn" depressed :loading="loading" @click="load">
          <v-icon left small>refresh</v-icon>Actualizar
        </v-btn>
      </div>
    </div>

    <v-layout row wrap class="rpt-filters">
      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="startMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="startDate"
            label="Desde"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="startDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="startMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="endMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="endDate"
            label="Hasta"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="endDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="endMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-select
          :items="laboratorioItems"
          v-model="labId"
          label="Laboratorio"
          prepend-icon="business"
          clearable
        ></v-select>
      </v-flex>

      <v-flex md3 sm6 xs12 v-if="!isDistributor">
        <v-select
          :items="distribuidorItems"
          v-model="distId"
          label="Distribuidora"
          prepend-icon="local_shipping"
          clearable
        ></v-select>
      </v-flex>
    </v-layout>

    <v-layout row wrap v-if="resumen">
      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile rpt-tile--hero">
          <span class="rpt-tile__label">Valor facturado (CIF)</span>
          <span class="rpt-tile__value">{{ money(resumen.cif) }}</span>
          <span class="rpt-tile__foot">
            {{ resumen.n }} facturas en el periodo
          </span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Ciclo promedio</span>
          <span class="rpt-tile__value">
            {{ format(resumen.avgCiclo) }}<small>días</small>
          </span>
          <span class="rpt-tile__foot">
            el más lento: {{ format(resumen.maxCiclo) }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Tasa de rechazo</span>
          <span class="rpt-tile__value">
            {{ format(resumen.pctRechazo) }}<small>%</small>
          </span>
          <span class="rpt-tile__foot">
            {{ resumen.rechazadas }} facturas rechazadas
          </span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Pendientes de liquidar</span>
          <span class="rpt-tile__value">
            {{ resumen.pendientes }}<small>facturas</small>
          </span>
          <span class="rpt-tile__foot">generadas dentro del periodo</span>
        </div>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg12 sm12 xs12 v-if="chartData">
        <v-widget content-bg="white" title="Valor facturado y rechazos por mes">
          <div slot="widget-content">
            <e-chart
              :path-option="chartOptions"
              width="95%"
              height="320px"
            ></e-chart>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>

    <!-- Dos tablas con la misma estructura: se alternan con un selector para
         no duplicar el bloque ni forzar a bajar la página. -->
    <v-layout row wrap>
      <v-flex lg12 sm12 xs12>
        <v-widget content-bg="white" title="Desempeño por cliente">
          <div slot="widget-content">
            <v-btn-toggle
              v-model="vista"
              mandatory
              class="elevation-0"
              style="margin-bottom:12px"
            >
              <v-btn flat value="laboratorios">Laboratorios</v-btn>
              <v-btn flat value="distribuidores">Distribuidoras</v-btn>
            </v-btn-toggle>

            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th>
                      {{
                        vista === 'laboratorios'
                          ? 'Laboratorio'
                          : 'Distribuidora'
                      }}
                    </th>
                    <th class="num">Facturas</th>
                    <th class="num">CIF</th>
                    <th class="num">Ciclo prom.</th>
                    <th class="num">Rechazo</th>
                    <th class="num">Pendientes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in filas" :key="c.nombre">
                    <td class="rpt-strong">{{ c.nombre }}</td>
                    <td class="num">{{ c.n }}</td>
                    <td class="num">{{ money(c.cif) }}</td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          c.avgCiclo > 7 ? 'rpt-pill--warn' : '',
                        ]"
                      >
                        {{ format(c.avgCiclo) }}
                      </span>
                    </td>
                    <td class="num">
                      <span :class="['rpt-pill', pillRechazo(c.pctRechazo)]">
                        {{ format(c.pctRechazo) }}%
                      </span>
                    </td>
                    <td class="num rpt-muted">{{ c.pendientes }}</td>
                  </tr>
                  <tr v-if="!filas.length">
                    <td colspan="6" class="rpt-empty">
                      Sin facturas en el periodo seleccionado.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import EChart from '@/components/chart/echart'
import VWidget from '@/components/VWidget'
import exportReport from '@/helpers/ReportExport'
import ReportFilters from './mixins/ReportFilters'

// Umbrales de la tasa de rechazo: bajo el 5% es normal, sobre 20% exige acción.
const RECHAZO_ALERTA = 20
const RECHAZO_OK = 5

const COLUMNS = [
  { key: 'nombre', label: 'Cliente' },
  { key: 'n', label: 'Facturas' },
  { key: 'cif', label: 'CIF' },
  { key: 'peso', label: 'Peso bruto' },
  { key: 'avgCiclo', label: 'Ciclo promedio (días)' },
  { key: 'maxCiclo', label: 'Ciclo máximo (días)' },
  { key: 'rechazadas', label: 'Rechazadas' },
  { key: 'pctRechazo', label: '% rechazo' },
  { key: 'pendientes', label: 'Pendientes' },
]

export default {
  components: { VWidget, EChart },

  mixins: [ReportFilters],

  data() {
    return {
      vista: 'laboratorios',
      meta: null,
      resumen: null,
      laboratorios: [],
      distribuidores: [],
      tendencia: [],
      chartData: null,
      loading: false,
    }
  },

  computed: {
    filas() {
      return this.vista === 'laboratorios'
        ? this.laboratorios
        : this.distribuidores
    },

    chartOptions() {
      return [
        ['dataset.source', this.chartData],
        ['color', ['#254F87', '#BD614D']],
        ['legend.show', true],
        ['legend.bottom', 0],
        ['legend.icon', 'roundRect'],
        ['grid.left', 70],
        ['grid.right', 60],
        ['grid.top', 20],
        ['grid.bottom', 52],
        ['tooltip.trigger', 'axis'],
        ['tooltip.axisPointer.type', 'shadow'],
        ['xAxis.type', 'category'],
        // El wrapper oculta las etiquetas de eje por defecto: hay que pedirlas.
        ['xAxis.axisLabel.show', true],
        ['xAxis.axisTick.show', false],
        ['xAxis.axisLine.lineStyle.color', '#e2e8f0'],
        // Dos ejes: el dinero y el conteo de rechazos no comparten escala.
        //
        // El arreglo se asigna completo en una sola opción, no con rutas
        // `yAxis[0].x`: el wrapper aplica las rutas con lodash.set sobre un
        // yAxis que por defecto es un OBJETO, así que las rutas indexadas le
        // agregarían las claves '0' y '1' en vez de convertirlo en arreglo, y
        // el gráfico no llega a dibujarse.
        [
          'yAxis',
          [
            {
              type: 'value',
              name: 'CIF',
              nameTextStyle: { color: '#64748b' },
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: { show: true },
              splitLine: { lineStyle: { color: '#eef2f7' } },
            },
            {
              type: 'value',
              name: 'rechazos',
              nameTextStyle: { color: '#bd614d' },
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: { show: true },
              splitLine: { show: false },
            },
          ],
        ],
        ['series[0].type', 'bar'],
        ['series[0].barMaxWidth', 28],
        ['series[0].itemStyle.barBorderRadius', [4, 4, 0, 0]],
        // Los rechazos van como línea sobre el eje derecho: es una tasa que se
        // sigue en el tiempo, no un volumen que se compara por área.
        ['series[1].type', 'line'],
        ['series[1].yAxisIndex', 1],
        ['series[1].smooth', true],
        ['series[1].symbolSize', 6],
        ['series[1].lineStyle.width', 2],
      ]
    },
  },

  watch: {
    labId() {
      this.load()
    },
    distId() {
      this.load()
    },
    startDate() {
      this.load()
    },
    endDate() {
      this.load()
    },
  },

  created() {
    this.loadCatalogos()
    this.load()
  },

  methods: {
    pillRechazo(pct) {
      if (pct === null || pct === undefined) return ''
      if (pct >= RECHAZO_ALERTA) return 'rpt-pill--warn'
      if (pct <= RECHAZO_OK) return 'rpt-pill--ok'
      return 'rpt-pill--soft'
    },

    load() {
      this.chartData = null
      this.loading = true

      this.$store
        .dispatch('reports/getClientes', this.paramsConFechas())
        .then(response => {
          const data = response && response.data
          if (!data) return
          this.meta = data.meta
          this.resumen = data.resumen
          this.laboratorios = data.laboratorios || []
          this.distribuidores = data.distribuidores || []
          this.tendencia = data.tendencia || []
          this.chartData = this.tendencia.map(row => ({
            mes: row.label,
            CIF: row.cif,
            Rechazos: row.rechazadas,
          }))
        })
        .finally(() => {
          this.loading = false
        })
    },

    exportar() {
      exportReport('clientes', [
        { name: 'Laboratorios', rows: this.laboratorios, columns: COLUMNS },
        { name: 'Distribuidoras', rows: this.distribuidores, columns: COLUMNS },
        {
          name: 'Tendencia',
          rows: this.tendencia,
          columns: [
            { key: 'periodo', label: 'Periodo' },
            { key: 'n', label: 'Facturas' },
            { key: 'cif', label: 'CIF' },
            { key: 'rechazadas', label: 'Rechazadas' },
          ],
        },
      ])
    },
  },
}
</script>
